/**
 * Background Translator Service
 * Pre-translates pages in the background when language changes
 */

import pageTranslatorService from './pageTranslatorService';

class BackgroundTranslatorService {
  constructor() {
    this.translatedPages = new Set(); // Track which pages are translated
    this.isTranslating = false;
    this.translationQueue = [];
    this.loadFromStorage();
  }

  /**
   * Load translated pages from localStorage
   */
  loadFromStorage() {
    try {
      const saved = localStorage.getItem('translatedPages');
      if (saved) {
        const pages = JSON.parse(saved);
        pages.forEach(page => this.translatedPages.add(page));
        console.log(`[Background Translator] Loaded ${pages.length} translated pages from storage`);
      }
    } catch (error) {
      console.error('[Background Translator] Error loading from storage:', error);
    }
  }

  /**
   * Save translated pages to localStorage
   */
  saveToStorage() {
    try {
      const pages = Array.from(this.translatedPages);
      localStorage.setItem('translatedPages', JSON.stringify(pages));
    } catch (error) {
      console.error('[Background Translator] Error saving to storage:', error);
    }
  }

  /**
   * Mark a page as translated
   */
  markPageAsTranslated(route, language) {
    const key = `${route}-${language}`;
    this.translatedPages.add(key);
    this.saveToStorage(); // Persist to localStorage
  }

  /**
   * Check if a page is already translated
   */
  isPageTranslated(route, language) {
    const key = `${route}-${language}`;
    return this.translatedPages.has(key);
  }

  /**
   * Get all routes that need translation
   */
  getAllRoutes() {
    // Common routes in the application
    return [
      '/',
      '/products',
      '/about',
      '/contact',
      '/certificates',
      '/cart',
      '/checkout',
      '/category/himalayan-pink-salt',
      '/category/onyx-marble-granite',
      '/category/precious-stones',
      '/category/apparel',
      '/shipping-options-policy',
      '/shipping-costs-policy',
      '/delivery-times-policy',
      '/return-policy',
      '/refund-policy',
      '/payment-terms-policy',
      '/privacy-policy',
    ];
  }

  /**
   * Start background translation of all pages
   * Since we can't navigate to pages in background, we'll prepare translation data
   * and ensure pages translate immediately when visited
   */
  async startBackgroundTranslation(targetLanguage, sourceLanguage = 'en') {
    if (this.isTranslating) {
      console.log('[Background Translator] Already translating, skipping...');
      return;
    }

    if (targetLanguage === 'en' || targetLanguage === sourceLanguage) {
      return;
    }

    this.isTranslating = true;
    console.log(`[Background Translator] Preparing translation system for ${targetLanguage}...`);

    const routes = this.getAllRoutes();
    const currentRoute = window.location.pathname + window.location.search;

    // Mark all routes as needing translation (they'll be translated when visited)
    routes.forEach(route => {
      if (!this.isPageTranslated(route, targetLanguage)) {
        // Store in queue for when user visits
        this.translationQueue.push({ route, targetLanguage, sourceLanguage });
      }
    });

    console.log(`[Background Translator] Prepared ${this.translationQueue.length} pages for translation`);
    console.log(`[Background Translator] Pages will translate automatically when visited`);

    this.isTranslating = false;
  }

  /**
   * Check if a route needs translation and get translation params
   */
  getTranslationParams(route, language) {
    const queued = this.translationQueue.find(item => item.route === route && item.targetLanguage === language);
    if (queued) {
      // Remove from queue since we're translating it now
      this.translationQueue = this.translationQueue.filter(item => 
        !(item.route === route && item.targetLanguage === language)
      );
      return { needsTranslation: true, sourceLanguage: queued.sourceLanguage };
    }
    return { needsTranslation: !this.isPageTranslated(route, language), sourceLanguage: 'en' };
  }

  /**
   * Clear translation cache
   */
  clearCache() {
    this.translatedPages.clear();
    this.isTranslating = false;
    this.translationQueue = [];
    localStorage.removeItem('translatedPages'); // Clear from storage too
  }

  /**
   * Pre-translate a specific route (when user might visit it soon)
   */
  async preTranslateRoute(route, targetLanguage, sourceLanguage = 'en') {
    if (targetLanguage === 'en' || targetLanguage === sourceLanguage) {
      return;
    }

    if (this.isPageTranslated(route, targetLanguage)) {
      return; // Already translated
    }

    // Store in queue for background processing
    this.translationQueue.push({ route, targetLanguage, sourceLanguage });
  }
}

const backgroundTranslatorService = new BackgroundTranslatorService();
export default backgroundTranslatorService;

